import { getAppSettings } from '../firebase/services/appSettingsService';

/**
 * Service de génération de prompts optimisés via l'IA
 */
class PromptChatService {
  /**
   * System prompt pour guider l'IA dans la génération de prompts de résumé
   */
  getSystemPrompt() {
    return `Tu es un expert en création de prompts pour l'IA, spécialisé dans l'analyse et le résumé de réunions d'entreprise. Ton rôle est d'aider l'utilisateur à créer des prompts précis, efficaces et adaptés à ses besoins spécifiques.

CONTEXTE :
L'utilisateur souhaite générer des résumés automatiques de ses réunions. Le prompt qu'il va créer sera utilisé pour instruire une IA (GPT-4) à analyser des transcriptions de réunions et produire des résumés structurés.

OBJECTIFS :
- Créer des prompts clairs, spécifiques et détaillés
- Structurer les prompts pour obtenir des résumés cohérents
- Adapter le niveau de détail selon le contexte (réunion technique, commerciale, stratégique, etc.)
- Inclure des instructions sur le format de sortie souhaité

BONNES PRATIQUES À SUIVRE :
1. **Précision du contexte** : Spécifier le type de réunion, le secteur d'activité, l'audience cible
2. **Structure claire** : Définir des sections (résumé exécutif, points clés, décisions, actions, etc.)
3. **Ton et style** : Préciser le niveau de formalité, le public cible
4. **Éléments à extraire** : Participants, durée, objectifs, résultats, prochaines étapes
5. **Format de sortie** : Markdown, bullet points, tableau, etc.
6. **Longueur souhaitée** : Nombre de mots ou de pages approximatif

PROCESSUS :
1. Pose des questions pour comprendre le besoin précis
2. Identifie le type de réunion et le contexte
3. Détermine l'audience et l'usage du résumé
4. Propose un prompt structuré et personnalisé
5. Itère selon les retours de l'utilisateur

EXEMPLES DE QUESTIONS À POSER :
- Quel type de réunions résumes-tu habituellement ?
- Qui sont les destinataires de ces résumés ?
- Quel niveau de détail recherches-tu ?
- Y a-t-il des éléments spécifiques à toujours inclure ?
- Quel format préfères-tu (bullet points, paragraphes, sections) ?

Commence par saluer l'utilisateur et pose-lui quelques questions pour bien cerner ses besoins avant de proposer un prompt personnalisé.

IMPORTANT : Quand tu proposes un prompt final, structure-le clairement avec des marqueurs comme "PROMPT PROPOSÉ:" ou "Voici le prompt:" pour faciliter son extraction et son utilisation.`;
  }

  /**
   * Envoie une requête à l'API OpenAI pour générer un prompt optimisé
   * @param {Array} messages - Historique des messages de la conversation
   * @returns {Promise<string>} - Réponse de l'IA
   */
  async generatePromptFromChat(messages) {
    try {
      const settings = await getAppSettings();
      const apiKey = settings?.openAISettings?.apiKey;

      if (!apiKey) {
        throw new Error('Clé API OpenAI non configurée. Veuillez contacter l\'administrateur.');
      }

      // Préparer le contexte système
      const systemMessage = {
        role: 'system',
        content: this.getSystemPrompt()
      };

      // Construire la requête avec tout l'historique
      const apiMessages = [systemMessage, ...messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }))];

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: apiMessages,
          temperature: 0.7,
          max_tokens: 800, // Limite pour éviter des réponses trop longues
          presence_penalty: 0.1,
          frequency_penalty: 0.1
        })
      });

      if (!response.ok) {
        const error = await response.json();

        throw new Error(error.error?.message || 'Erreur lors de l\'appel à OpenAI');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Génère le message d'accueil pour commencer la conversation
   * @returns {string} - Message d'accueil
   */
  getWelcomeMessage() {
    return `Bonjour ! 👋 Je suis votre assistant spécialisé dans la création de prompts optimisés pour les résumés de réunions.

Je vais vous aider à créer un prompt personnalisé qui permettra à l'IA de générer des résumés parfaitement adaptés à vos besoins.

Pour commencer, j'aimerais mieux comprendre votre contexte :

🎯 **Quel type de réunions souhaitez-vous résumer ?**
- Réunions d'équipe / stand-up
- Réunions commerciales / client
- Réunions stratégiques / direction
- Formations / présentations
- Autre type ?

💼 **Dans quel contexte travaillez-vous ?**
- Secteur d'activité
- Taille d'équipe
- Fréquence des réunions

📋 **Quel usage ferez-vous de ces résumés ?**
- Communication interne
- Reporting hiérarchique
- Suivi d'actions
- Archive/documentation

N'hésitez pas à me donner ces informations pour que je puisse vous proposer un prompt sur-mesure ! 😊`;
  }

  /**
   * Extrait le prompt final d'une réponse d'IA
   * @param {string} aiResponse - Réponse complète de l'IA
   * @returns {string} - Prompt extrait et nettoyé
   */
  extractPromptFromResponse(aiResponse) {
    // Recherche de patterns courants pour identifier le prompt final
    const patterns = [
      /```(?:prompt|text)?\n([\s\S]*?)\n```/i,
      /PROMPT PROPOSÉ[:\s]*\n([\s\S]*?)(?:\n\n|\n---|\n===|$)/i,
      /PROMPT FINAL[:\s]*\n([\s\S]*?)(?:\n\n|\n---|\n===|$)/i,
      /Voici le prompt[:\s]*\n([\s\S]*?)(?:\n\n|\n---|\n===|$)/i,
      /Le prompt serait[:\s]*\n([\s\S]*?)(?:\n\n|\n---|\n===|$)/i,
      /Prompt pour l'IA[:\s]*\n([\s\S]*?)(?:\n\n|\n---|\n===|$)/i,
      // Pattern plus flexible pour des prompts qui commencent par "Vous êtes"
      /(Vous êtes[\s\S]*?)(?:\n\n### |\n\n## |\n\n---|\n\n===|$)/i
    ];

    for (const pattern of patterns) {
      const match = aiResponse.match(pattern);
      if (match && match[1]) {
        let extracted = match[1].trim();
        
        // Nettoyer les marqueurs de fin communs
        extracted = extracted.replace(/\n---+$/, '').replace(/\n===+$/, '').trim();
        
        // Nettoyer les titres indésirables au début
        const titlesToRemove = [
          /^Prompt pour l'IA\s*:?\s*/i,
          /^Voici le prompt\s*:?\s*/i,
          /^Le prompt serait\s*:?\s*/i,
          /^Prompt proposé\s*:?\s*/i,
          /^Prompt final\s*:?\s*/i
        ];
        
        for (const titlePattern of titlesToRemove) {
          extracted = extracted.replace(titlePattern, '').trim();
        }
        
        // S'assurer que c'est un vrai prompt, pas juste une phrase
        if (extracted.length > 50) {
          return extracted;
        }
      }
    }

    // Si aucun pattern spécifique n'est trouvé, essayer de trouver un texte qui commence par "Vous êtes" ou "Analysez"
    const directPromptMatch = aiResponse.match(/(Vous êtes.*?(?:\n\n|$)|Analysez.*?(?:\n\n|$))/i);
    if (directPromptMatch && directPromptMatch[1] && directPromptMatch[1].length > 50) {
      return directPromptMatch[1].trim();
    }

    // En dernier recours, retourner la réponse complète
    return aiResponse.trim();
  }

  /**
   * Valide qu'un prompt généré respecte les bonnes pratiques
   * @param {string} prompt - Prompt à valider
   * @returns {Object} - Résultat de validation avec suggestions
   */
  validatePrompt(prompt) {
    const validation = {
      isValid: true,
      warnings: [],
      suggestions: []
    };

    // Vérifications de base
    if (prompt.length < 50) {
      validation.warnings.push('Le prompt semble très court. Considérez ajouter plus de détails.');
    }

    if (prompt.length > 2000) {
      validation.warnings.push('Le prompt est très long. Considérez le raccourcir pour plus d\'efficacité.');
    }

    // Vérification de la structure
    const hasStructure = /format|structure|section|point/i.test(prompt);
    if (!hasStructure) {
      validation.suggestions.push('Considérez ajouter des instructions sur la structure du résumé souhaité.');
    }

    // Vérification du contexte
    const hasContext = /réunion|meeting|contexte|équipe/i.test(prompt);
    if (!hasContext) {
      validation.suggestions.push('Ajoutez plus de contexte sur le type de réunions concernées.');
    }

    return validation;
  }
}

export default new PromptChatService(); 